import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/fddClient";

/**
 * Top-bar badge showing live AI spend for the current server session.
 * Polls /api/ai-spend every 5s. Clicking opens a drill-down modal with
 * per-feature breakdown and a list of recent calls.
 *
 * Spend resets when the API server restarts (session-scope, in-memory).
 */

type ByFeature = {
  tag: string;
  calls: number;
  cost: number;
  input_tokens: number;
  output_tokens: number;
  cache_read: number;
  cache_write: number;
};

type SpendRecord = {
  timestamp: number;
  tag: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cache_read: number;
  cache_write: number;
  cost_usd: number;
};

type Snapshot = {
  total: number;
  callCount: number;
  byFeature: ByFeature[];
  recent: SpendRecord[];
  startedAt: number | null;
};

function fmtUsd(n: number): string {
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1)    return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function fmtInt(n: number): string {
  return n.toLocaleString("en-US");
}

function fmtTimeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export default function AiSpendBadge() {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSnap = async () => {
      try {
        const r = await apiFetch("/api/ai-spend", {}, { interactive: false });
        if (!r.ok) return;
        const j: Snapshot = await r.json();
        if (!cancelled) setSnap(j);
      } catch {
        // ignore — server may be restarting
      }
    };
    fetchSnap();
    const id = window.setInterval(fetchSnap, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const total = snap?.total ?? 0;
  const callCount = snap?.callCount ?? 0;

  const reset = async () => {
    await apiFetch("/api/ai-spend/reset", { method: "POST" });
    const r = await apiFetch("/api/ai-spend");
    if (r.ok) setSnap(await r.json());
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={`${callCount} Claude calls this session — click for breakdown`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "#eef6ea",
          border: "1px solid #dbead7",
          borderRadius: 999,
          padding: "5px 10px",
          cursor: "pointer",
          fontFamily: "Arial",
          fontSize: 12,
          fontWeight: 700,
          color: "#2f6b2f",
        }}
      >
        <span aria-hidden style={{ fontSize: 11 }}>AI</span>
        <span>{fmtUsd(total)}</span>
        <span style={{ color: "#86b37d", fontWeight: 500 }}>· {callCount}</span>
      </button>

      {open && snap && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.3)",
            zIndex: 50,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "60px 16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              width: "min(720px, 100%)",
              maxHeight: "80vh",
              overflow: "auto",
              fontFamily: "Arial",
              fontSize: 13,
              color: "#2c3e2c",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 18px",
                borderBottom: "1px solid #eef6ea",
                position: "sticky",
                top: 0,
                background: "#fff",
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#2f6b2f" }}>
                  AI spend this session
                </div>
                <div style={{ fontSize: 11, color: "#6a8a6a", marginTop: 2 }}>
                  {fmtUsd(snap.total)} across {snap.callCount} Claude calls — resets when the API server restarts
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={reset}
                  style={{
                    background: "#fff",
                    border: "1px solid #dbead7",
                    color: "#2f6b2f",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  Reset
                </button>
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: "#fff",
                    border: "1px solid #dbead7",
                    color: "#2f6b2f",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={{ padding: "12px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2f6b2f", marginBottom: 6 }}>
                By feature
              </div>
              {snap.byFeature.length === 0 ? (
                <div style={{ color: "#86b37d", fontStyle: "italic", padding: "10px 0" }}>
                  No Claude calls yet this session.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#6a8a6a", borderBottom: "1px solid #eef6ea" }}>
                      <th style={{ padding: "6px 4px", fontWeight: 600 }}>Feature</th>
                      <th style={{ padding: "6px 4px", fontWeight: 600, textAlign: "right" }}>Calls</th>
                      <th style={{ padding: "6px 4px", fontWeight: 600, textAlign: "right" }}>Input</th>
                      <th style={{ padding: "6px 4px", fontWeight: 600, textAlign: "right" }}>Cache R/W</th>
                      <th style={{ padding: "6px 4px", fontWeight: 600, textAlign: "right" }}>Output</th>
                      <th style={{ padding: "6px 4px", fontWeight: 600, textAlign: "right" }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snap.byFeature.map((f) => (
                      <tr key={f.tag} style={{ borderBottom: "1px solid #f6faf4" }}>
                        <td style={{ padding: "6px 4px", fontWeight: 700 }}>{f.tag}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right" }}>{fmtInt(f.calls)}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right" }}>{fmtInt(f.input_tokens)}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", color: "#6a8a6a" }}>
                          {fmtInt(f.cache_read)} / {fmtInt(f.cache_write)}
                        </td>
                        <td style={{ padding: "6px 4px", textAlign: "right" }}>{fmtInt(f.output_tokens)}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", fontWeight: 700 }}>
                          {fmtUsd(f.cost)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: "2px solid #dbead7" }}>
                      <td style={{ padding: "8px 4px", fontWeight: 800, color: "#2f6b2f" }}>Total</td>
                      <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 800 }}>
                        {fmtInt(snap.callCount)}
                      </td>
                      <td colSpan={3} />
                      <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 800, color: "#2f6b2f" }}>
                        {fmtUsd(snap.total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}

              {snap.recent.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#2f6b2f", marginTop: 18, marginBottom: 6 }}>
                    Recent calls (last {snap.recent.length})
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "#6a8a6a", borderBottom: "1px solid #eef6ea" }}>
                        <th style={{ padding: "5px 4px", fontWeight: 600 }}>When</th>
                        <th style={{ padding: "5px 4px", fontWeight: 600 }}>Feature</th>
                        <th style={{ padding: "5px 4px", fontWeight: 600 }}>Model</th>
                        <th style={{ padding: "5px 4px", fontWeight: 600, textAlign: "right" }}>In / Out</th>
                        <th style={{ padding: "5px 4px", fontWeight: 600, textAlign: "right" }}>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snap.recent.map((r, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f6faf4" }}>
                          <td style={{ padding: "5px 4px", color: "#6a8a6a" }}>{fmtTimeAgo(r.timestamp)}</td>
                          <td style={{ padding: "5px 4px" }}>{r.tag}</td>
                          <td style={{ padding: "5px 4px", color: "#6a8a6a" }}>{r.model}</td>
                          <td style={{ padding: "5px 4px", textAlign: "right" }}>
                            {fmtInt(r.input_tokens + r.cache_read + r.cache_write)} / {fmtInt(r.output_tokens)}
                          </td>
                          <td style={{ padding: "5px 4px", textAlign: "right" }}>{fmtUsd(r.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
